import { useGetCategoryContent } from '#/api/generated/content/content';
import type { ContentPage } from '#/api/generated/fINRAMarketDataAPI.schemas';
import type { ContentArticleCard, ContentLink } from '#/content/lib';
import {
  contentArticleCard,
  contentLink,
  contentSection,
  contentTextPlain,
  drupalLinkHref,
} from '#/content/lib';
import { CONTENT_CACHE_QUERY_POLICY } from '#/content/queries';
import { isSuccessResponse } from '#/lib/api-client';

/**
 * The category-page content document: view model, merge, and hook.
 *
 * Every field degrades to `null` (tier 2) — a missing section drops out and
 * the page still renders. There is no tier-1 fallback object: the only
 * structural field, `pageTitle`, is coalesced at the call site against the
 * category label already in `src/lib/category-navigation.ts` (derive, don't
 * duplicate), so no evergreen copy lives here.
 *
 * @TODO(nlong): This represents the fields entered so far. Revisit as more
 * Drupal fields gain content (e.g. the video and browse-securities sections).
 */
export interface CategoryContentView {
  /** page_title ?? title; null when unauthored — caller supplies the label. */
  pageTitle: string | null;
  /** Lead paragraph under the page title; plain text, drops when empty. */
  intro: string | null;
  /**
   * A "read more in-depth" link shown between the intro and related articles.
   * Null when unauthored or the link can't resolve to a renderable href.
   */
  inDepthLink: ContentLink | null;
  relatedArticles: ContentArticleCard[] | null;
  learning: CategoryLearningView | null;
  toolsResources: CategoryToolCard[] | null;
}

/** One title + text pair in the learning component. */
export interface CategoryLearningItem {
  title: string;
  text: string;
}

/**
 * The learning component: a centered header followed by up-to-three columns.
 * Sourced from the `information` list — the first item is the header, the rest
 * flow in as columns.
 */
export interface CategoryLearningView {
  header: CategoryLearningItem;
  columns: CategoryLearningItem[];
}

/** A tools-and-resources card: a heading, body copy, link, and Drupal icon. */
export interface CategoryToolCard {
  /** Drupal paragraph id — stable render key. */
  id: string;
  title: string;
  /** Body copy under the title; plain text, null when editors leave it empty. */
  body: string | null;
  /** Raw Drupal `icon_name`; the section maps it to a renderable icon. */
  icon: string | null;
  /**
   * Resolved href. Falls back to the FINRA fund analyzer tool when the Drupal
   * URI can't resolve, so the title always links somewhere.
   */
  href: string | null;
}

/**
 * Normalize the `articles` section into renderable teaser cards. Each
 * `mdc_articles_section` wraps a single `node--article` in a nested section,
 * so flatten two levels down before building cards. Null when none remain.
 */
function normalizeRelatedArticles(
  page: ContentPage | null | undefined,
): ContentArticleCard[] | null {
  const items = contentSection(page, 'articles')?.items ?? [];
  const cards = items
    .flatMap((item) => item.sections ?? [])
    .flatMap((section) => section.items ?? [])
    .flatMap((article) => {
      const card = contentArticleCard(article);
      return card ? [card] : [];
    });

  return cards.length > 0 ? cards : null;
}

/**
 * Normalize the `information` list into the learning component: the first
 * item with both a title and text is the header, the rest are columns. Items
 * missing either field drop; null when no item is renderable.
 */
function normalizeLearning(
  page: ContentPage | null | undefined,
): CategoryLearningView | null {
  const items = contentSection(page, 'information')?.items ?? [];
  const entries = items.flatMap((item) => {
    const title = item.title?.trim();
    const text = contentTextPlain(item.text);
    return title && text ? [{ title, text }] : [];
  });

  const [header, ...columns] = entries;
  return header ? { header, columns } : null;
}

/**
 * Normalize the `tools` section into resource cards. A card needs a title; its
 * body copy drops when empty, and its href falls back to the FINRA fund
 * analyzer tool when the Drupal URI can't resolve (e.g. `entity:node/…`). Null
 * when no card is renderable.
 */
function normalizeToolsResources(
  page: ContentPage | null | undefined,
): CategoryToolCard[] | null {
  const items = contentSection(page, 'tools')?.items ?? [];
  const cards = items.flatMap((item) => {
    const title = item.title?.trim();
    if (!title) {
      return [];
    }

    return [
      {
        id: item.id,
        title,
        body: contentTextPlain(item.text),
        icon: item.icon?.icon_name?.trim() || null,
        href: drupalLinkHref(item.link?.uri) || 'https://tools.finra.org/fund_analyzer/',
      },
    ];
  });

  return cards.length > 0 ? cards : null;
}

/**
 * Merge a category content page into the view model. `null`/`undefined` — a
 * total content outage — yields an all-null view: the page renders from the
 * caller-supplied label with every optional section dropped.
 */
export function mergeCategory(
  page: ContentPage | null | undefined,
): CategoryContentView {
  return {
    pageTitle: page?.page_title?.trim() || page?.title?.trim() || null,
    intro: contentTextPlain(page?.intro),
    inDepthLink: contentLink(page?.in_depth_link),
    relatedArticles: normalizeRelatedArticles(page),
    learning: normalizeLearning(page),
    toolsResources: normalizeToolsResources(page),
  };
}

interface UseCategoryContentResult {
  content: CategoryContentView;
  /** True when the content API failed and the page renders unadorned. */
  isDegraded: boolean;
}

/**
 * Category-page content, merged against an all-null view on failure.
 *
 * Warmed via `categoryContentOptions(category)` on `/$category` with
 * `staleTime: Infinity` for no re-renders for the life of the session.
 */
export function useCategoryContent(category: string): UseCategoryContentResult {
  const query = useGetCategoryContent(category, {
    query: {
      ...CONTENT_CACHE_QUERY_POLICY,
      select: (res) => mergeCategory(isSuccessResponse(res) ? res.data : null),
    },
  });

  return {
    content: query.data ?? mergeCategory(null),
    isDegraded: query.isError,
  };
}
